import { useCallback, useMemo, useState } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack, Switch } from 'src/core/ui';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { getAllInPriceFromListPriceForListing } from 'src/utils/inventoryUtils';
import {
  BulkListingPriceCalculation,
  CompListing,
  Listing,
  ListingBroadcastStatus,
  ListingPriceCalculation,
} from 'src/WebApiController';

import { ComparableListing, ComparableListings } from '../ComparableListings';
import { ListingsCarousel } from '../ListingsCarousel/ListingsCarousel';
import * as styles from './CompListingsPreview.css';

export type CompListingsPreviewV2Props = {
  isLoading: boolean;
  showListingsCarousel?: boolean;
  listings: Listing[]; // Seller listings
  selectedListingIds: number[]; // Your selected listing ids
  listingPriceCalculations: BulkListingPriceCalculation[];
  compListings: CompListing[];
  isExcludingFanInventory: boolean;
};

export const CompListingsPreviewV2 = ({
  isLoading,
  showListingsCarousel,
  listings,
  selectedListingIds,
  listingPriceCalculations,
  compListings,
  isExcludingFanInventory,
}: CompListingsPreviewV2Props) => {
  const [selectedListing, setSelectedListing] = useState<Listing | null>(
    listings.length == 1 ? listings[0] : null
  );

  const [showOnlyMyListings, setShowOnlyMyListings] = useState(false);

  const listingPriceCalculationsByListingId = useMemo(() => {
    const record: Record<number, BulkListingPriceCalculation> = {};
    listingPriceCalculations.forEach((x) => {
      record[x.listingId] = x;
    });

    return record;
  }, [listingPriceCalculations]);

  const selectedListingPriceCalculation = useMemo(
    () =>
      selectedListing
        ? listingPriceCalculationsByListingId[selectedListing.id]
        : null,
    [listingPriceCalculationsByListingId, selectedListing]
  );

  const allSellerAccountPosListingIds = useMemo(() => {
    return new Set(
      listingPriceCalculations.flatMap((c) => c.sellerAccountPosListingIds)
    );
  }, [listingPriceCalculations]);

  const sanitizedSelectedListingIds = useMemo(() => {
    if (selectedListing) {
      return [selectedListing.id];
    }
    return selectedListingIds;
  }, [selectedListing, selectedListingIds]);

  const mapListingToComparableListing = useCallback(
    (listing: Listing): ComparableListing => {
      const listingPreview = listingPriceCalculationsByListingId[listing.id];

      const websitePrice =
        listingPreview?.websitePrice?.amt ??
        getAllInPriceFromListPriceForListing(listing);

      const autoPriceResultType = listingPreview?.autoPricingResultType;
      const autoPricingResultConstraintType =
        listingPreview?.autoPricingConstraintType;

      return {
        listingId: listing.id,
        section: listing.seating.section,
        row: listing.seating.row ?? undefined,
        seatFr: listing.seating.seatFr ?? undefined,
        seatTo: listing.seating.seatTo ?? undefined,
        price: websitePrice,
        unitCost: listing.unitCst?.amt ?? undefined,
        currencyCode: listing.currency,
        listingNotes: listing.seatTraits ?? undefined,
        // Price assuming same fees as seller’s account
        sellerNetProceeds: listing.listPrice,
        isYourListing: sanitizedSelectedListingIds.includes(listing.id),
        isSellerListing: true,
        isListed: listing.brdcstStatus === ListingBroadcastStatus.Broadcast,
        availableTickets: listing.availQty,
        validPurchaseQuantities: listing.vldPurQty,
        isSelectedWithListingGroup: false,
        autoPricingResultType: autoPriceResultType ?? undefined,
        autoPricingResultConstraintType:
          autoPricingResultConstraintType ?? undefined,
        shouldShowResultTypeAction: listing.isAutoPrc ?? false,
      };
    },
    [listingPriceCalculationsByListingId, sanitizedSelectedListingIds]
  );

  const isComparableToSelectedListing = useCallback(
    (cl: CompListing) => {
      if (cl.isSellerCompListing) {
        return (
          selectedListingPriceCalculation?.sellerCompStubHubListingIds.includes(
            cl.listingId
          ) ?? false
        );
      }

      const isComparableListing =
        selectedListingPriceCalculation?.compStubHubListingIds.includes(
          cl.listingId
        ) ?? false;
      const isOutlierListing =
        selectedListingPriceCalculation?.outlierStubHubListingIds.includes(
          cl.listingId
        ) ?? false;

      return !selectedListing || isComparableListing || isOutlierListing;
    },
    [
      selectedListing,
      selectedListingPriceCalculation?.compStubHubListingIds,
      selectedListingPriceCalculation?.outlierStubHubListingIds,
      selectedListingPriceCalculation?.sellerCompStubHubListingIds,
    ]
  );

  const mapCompListingToComparableListing = useCallback(
    (cl: CompListing): ComparableListing => {
      const isAnchor = selectedListingPriceCalculation?.udrRefLstIds?.includes(
        cl.listingId
      );
      const isOutlier =
        selectedListingPriceCalculation?.outlierStubHubListingIds.includes(
          cl.listingId
        ) ?? false;

      return {
        listingId: cl.listingId,
        section: cl.section ?? undefined,
        row: cl.row ?? undefined,
        seatFr: cl.seatFrom ?? undefined,
        seatTo: cl.seatTo ?? undefined,
        price: cl.sellerAllInPrice?.amt,
        priceDisp: cl.sellerAllInPrice?.disp,
        currencyCode: cl.sellerAllInPrice?.currency ?? undefined,
        listingNotes: cl.listingNotes ?? undefined,
        // Price assuming same fees as seller’s account
        sellerNetProceeds: cl.sellerNetProceeds?.amt ?? undefined,
        isAnchor: isAnchor,
        isOutlier: isOutlier,
        isYourListing: false,
        isSellerListing: false,
        isFanInventory: cl.isFanInventory ?? false,
        availableTickets: cl.availableTickets ?? undefined,
        validPurchaseQuantities: cl.validPurchaseQuantities ?? undefined,
        isSelectedWithListingGroup: false,
      };
    },
    [
      selectedListingPriceCalculation?.outlierStubHubListingIds,
      selectedListingPriceCalculation?.udrRefLstIds,
    ]
  );

  const sellerListings = useMemo(() => {
    const sellerAccountPosListingIds = selectedListingPriceCalculation
      ? new Set(selectedListingPriceCalculation.sellerAccountPosListingIds)
      : allSellerAccountPosListingIds;

    return listings
      .filter(
        (l) =>
          sellerAccountPosListingIds.has(l.id) ||
          sanitizedSelectedListingIds.includes(l.id)
      )
      .map((l) => mapListingToComparableListing(l));
  }, [
    allSellerAccountPosListingIds,
    listings,
    mapListingToComparableListing,
    sanitizedSelectedListingIds,
    selectedListingPriceCalculation,
  ]);

  const currentComparableListings = useMemo(() => {
    return compListings
      .filter(isComparableToSelectedListing)
      .map(mapCompListingToComparableListing);
  }, [
    compListings,
    isComparableToSelectedListing,
    mapCompListingToComparableListing,
  ]);

  const listingPriceCalcWithComps = useMemo(
    (): ListingPriceCalculation | null =>
      selectedListingPriceCalculation
        ? {
            ...selectedListingPriceCalculation,
            compListings,
          }
        : null,
    [compListings, selectedListingPriceCalculation]
  );

  const hasBetterPrice = useMemo(() => {
    const hasBetterPrice = (listingPriceCalc: BulkListingPriceCalculation) => {
      if (
        !listingPriceCalc?.newPrcInclFanInv ||
        !listingPriceCalc?.websitePrice?.amt
      ) {
        return false;
      }
      return (
        listingPriceCalc.newPrcInclFanInv < listingPriceCalc.websitePrice.amt
      );
    };

    if (selectedListing) {
      const listingPriceCalc = listingPriceCalculations.find(
        (lpc) => lpc.listingId === selectedListing.id
      );
      return listingPriceCalc ? hasBetterPrice(listingPriceCalc) : false;
    }

    return listingPriceCalculations.some(hasBetterPrice);
  }, [listingPriceCalculations, selectedListing]);

  return isLoading ? (
    <PosSpinner />
  ) : (
    <Stack direction="column" className={styles.root}>
      {listings.length > 1 && showListingsCarousel && (
        <ListingsCarousel
          listings={listings}
          selectedListing={selectedListing}
          setSelectedListing={setSelectedListing}
        />
      )}

      <Stack
        direction="row"
        gap="m"
        alignItems="center"
        justifyContent="spaceBetween"
      >
        <span>
          <Content id={ContentId.OnlyShowMyListings} />
        </span>
        <Switch
          checked={showOnlyMyListings}
          onChange={(e) => e.stopPropagation()}
          onCheckedChange={(isChecked) => {
            setShowOnlyMyListings(isChecked);
          }}
        />
      </Stack>

      {isExcludingFanInventory && hasBetterPrice && (
        <div className={styles.fanInventoryInfoContainer}>
          <InfoOutlineIcon
            fill={vars.color.textWarning}
            size={vars.iconSize.l}
          />
          <span className={styles.statementText}>
            {selectedListing ? (
              <Content id={ContentId.NonCompetitiveListing} />
            ) : (
              <Content id={ContentId.NonCompetitiveListingBulk} />
            )}
          </span>
        </div>
      )}

      <ComparableListings
        currentListings={sellerListings}
        comparableListings={currentComparableListings}
        sortBy="price"
        showOnlyMyListings={showOnlyMyListings}
        listingPriceCalc={listingPriceCalcWithComps}
        listingPriceCalculations={listingPriceCalculations}
        floorPrice={selectedListingPriceCalculation?.cmpFlr?.amt}
        ceilingPrice={selectedListingPriceCalculation?.cmpCeil?.amt}
        isExcludingFanInventory={isExcludingFanInventory}
      />
    </Stack>
  );
};
